import type { TableExportDoc, TableExportSheet } from '@goyone/shared';
import { api, qs } from './api';

export type {
  TableExportColumn,
  TableExportDoc,
  TableExportRow,
  TableExportSection,
  TableExportSheet,
} from '@goyone/shared';

/**
 * تصديرُ جدولٍ معروضٍ — إكسل أو PDF — من أيّ صفحة.
 *
 * الصفحةُ تبني `TableExportDoc` من صفوفها (الشكلُ في @goyone/shared)،
 * وهنا الطريقان:
 *
 * - إكسل: يُرسل المستندُ إلى `/reports/export-table` فيعود ملفُّ xlsx حقيقيّ
 *   (أرقامٌ أرقامًا بتنسيقها، أوراقٌ من اليمين). مكتبةُ إكسل على الخادم، فلا
 *   تُحمَّل على كلّ زائر.
 * - PDF: يُحفظ المستندُ محلّيًّا وتفتحه صفحةُ الطباعة `/print/table`، والمتصفّحُ
 *   يحفظه PDF — العربيّةُ موصولةٌ كما يرسمها هو، لا حروفًا مقطّعة.
 *
 * والأيسرُ استعمالُ `<ExportMenu>` (components/ExportMenu) لا هذه مباشرة.
 */

/** تنزيلُ المستند إكسل. عبر `api` لا رابطٍ مباشر: رمزُ الوصول في الترويسة. */
export async function exportTableXlsx(doc: TableExportDoc): Promise<void> {
  const blob = await api<Blob>('/reports/export-table', { method: 'POST', body: doc, raw: true });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${doc.title.replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'تقرير'}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/*
 * المستندُ إلى صفحة الطباعة عبر التخزين المحلّيّ لا الرابط.
 *
 * قائمةٌ بآلاف الصفوف لا تتّسع لها عنوانُ صفحة. والتخزين المحلّيّ (لا
 * الجلسة) لأنّ صفحة الطباعة قد تُفتح في تبويبٍ آخر. ويُبقى آخرُ بضعةٍ فقط
 * كي لا يتراكم ما طُبع.
 */
const PREFIX = 'goyone:print-table:';
const KEEP = 4;

/** يحفظ المستند ويعيد مسار صفحة الطباعة الخاصّ به. */
export function printTablePath(doc: TableExportDoc): string {
  const id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
  try {
    const keys = Object.keys(localStorage)
      .filter((k) => k.startsWith(PREFIX))
      .sort();
    for (const k of keys.slice(0, Math.max(0, keys.length - (KEEP - 1)))) localStorage.removeItem(k);
    localStorage.setItem(PREFIX + id, JSON.stringify(doc));
  } catch {
    /* تخزينٌ ممتلئٌ أو محجوب: صفحة الطباعة تقول إنّ المستند غير متاح. */
  }
  return `/print/table?k=${id}`;
}

/** تقرؤه صفحةُ الطباعة. */
export function readPrintTable(id: string | null): TableExportDoc | null {
  if (!id) return null;
  try {
    const raw = localStorage.getItem(PREFIX + id);
    return raw ? (JSON.parse(raw) as TableExportDoc) : null;
  } catch {
    return null;
  }
}

/* ==========================================================================
   تصديرُ قائمةٍ مصفّاة — كلُّ صفحاتها لا المعروضةُ وحدها
   ========================================================================== */

/** سقفُ صفوف القسم الواحد في المستند (`rows.max` في TableExportSectionSchema). */
export const EXPORT_ROW_LIMIT = 20_000;

/**
 * يجلب كلَّ صفوف قائمةٍ مقسّمة صفحاتٍ بشروط الصفحة نفسها.
 *
 * `query` هو كائنُ الاستعلام الذي تجلب به الصفحةُ جدولها — البحث وفلاتر
 * الأعمدة (`f`) والمدى والفرز وبقيّة الشروط — فيُعاد كما هو صفحةً صفحة
 * بأكبر حجمٍ يقبله الخادم (`pageSize`: سقفُ مخطّط القائمة هناك)، حتى آخر
 * صفحة أو السقف. فالملفُّ هو ما يراه المستخدم مصفًّى، كاملًا.
 */
export async function fetchAllRows<T>(
  path: string,
  query: Record<string, unknown>,
  pageSize = 100,
  limit = EXPORT_ROW_LIMIT,
): Promise<{ items: T[]; total: number; truncated: boolean }> {
  const items: T[] = [];
  let total = 0;
  for (let page = 1; ; page++) {
    const res = await api<{ items: T[]; total: number; pageCount: number }>(
      `${path}${qs({ ...query, page, pageSize })}`,
    );
    total = res.total;
    items.push(...res.items);
    if (page >= res.pageCount || res.items.length === 0 || items.length >= limit) break;
  }
  return { items: items.slice(0, limit), total, truncated: total > limit };
}

/** مدةُ الرأس من «من/إلى» الصفحة، أو «كل الفترات» حين لا مدى. */
export function exportPeriod(from?: string, to?: string): TableExportSheet['period'] {
  return from || to ? { from: from || undefined, to: to || undefined } : { label: 'كل الفترات' };
}

/**
 * تاريخٌ للخليّة: يومُه بالتوقيت المحلّيّ (سنة-شهر-يوم).
 * لا `toISOString` — يرجع يومًا في مصر قبل الثانية صباحًا.
 */
export function exportDay(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** رقمٌ للخليّة من قيمة الخادم (المبالغ تصل نصوصًا عشريّة). */
export function exportNum(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** سطورُ الملاحظات: البحثُ والشروطُ النشطة وعددُ الصفوف — وتنبيهٌ إن قُصّت. */
export function exportNotes({
  q,
  conditions = [],
  count,
  total,
  truncated,
  unit = 'الصفوف',
}: {
  q?: string;
  conditions?: (string | false | null | undefined)[];
  count: number;
  total: number;
  truncated: boolean;
  /** ما يُعدّ: «الصفوف»، «القيود»… */
  unit?: string;
}): string[] {
  const n = (v: number) => v.toLocaleString('ar-EG-u-nu-latn');
  const out: string[] = [];
  if (q) out.push(`البحث: «${q}»`);
  for (const c of conditions) if (c) out.push(c);
  out.push(
    truncated
      ? `صُدِّر أوّل ${n(count)} من ${n(total)} من ${unit} — ضيّق المدى أو الفلاتر لتصدير الباقي.`
      : `عدد ${unit}: ${n(count)}`,
  );
  return out.slice(0, 20).map((s) => s.slice(0, 500));
}
